import AiIcon from "../../Core/AiIcon/AiIcon";
import React, { useEffect, useRef } from "react";
import { AlertCircle, History } from "lucide-react";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import {
	AiApprovalDecision,
	AiConversation as AiConversationModel,
} from "../../../models/AiAssistant";
import AiApprovalCard from "./AiApprovalCard";
import AiMessage from "./AiMessage";

export const SUGGESTED_PROMPTS = [
	"Hur många uppdrag skapades förra månaden?",
	"Vilka tidrapporter väntar på attest?",
	"Visa uppdrag som saknar konsult den här veckan",
];

interface AiConversationProps {
	conversation?: AiConversationModel;
	/** The sidebar has not yet decided which conversation to show. */
	isResolving?: boolean;
	/** Newest earlier thread, offered in the empty state as "Fortsätt senaste". */
	previousConversation?: AiConversationModel;
	onResume?: (conversationId: string) => void;
	/** A reply is being generated (message sent or approvals answered). */
	isThinking?: boolean;
	isApproving?: boolean;
	error?: string;
	onRetry?: () => void;
	onSuggestion: (text: string) => void;
	onRespondToApprovals: (decisions: AiApprovalDecision[]) => void;
	className?: string;
}

/**
 * The scrolling thread: turns, the pending approval card, a thinking
 * indicator and an inline error. Keeps the newest content in view as it
 * arrives.
 */
const AiConversation: React.FC<AiConversationProps> = ({
	conversation,
	isResolving,
	previousConversation,
	onResume,
	isThinking,
	isApproving,
	error,
	onRetry,
	onSuggestion,
	onRespondToApprovals,
	className,
}) => {
	const bottomRef = useRef<HTMLDivElement>(null);
	const turnCount = conversation?.turns.length ?? 0;
	const pendingCount = conversation?.pendingApprovals.length ?? 0;

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
	}, [turnCount, pendingCount, isThinking, error]);

	if (isResolving && !conversation) {
		return (
			<div className={cn("flex h-full items-center justify-center", className)} aria-busy="true">
				<div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand">
					<AiIcon className="h-5 w-5 animate-pulse" />
				</div>
			</div>
		);
	}

	if (!conversation || turnCount === 0) {
		return (
			<div
				className={cn(
					"flex h-full flex-col items-center justify-center px-6 text-center",
					className
				)}
			>
				<div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand">
					<AiIcon className="h-5 w-5" />
				</div>
				<h3 className="text-base font-semibold text-foreground">Vad kan jag hjälpa till med?</h3>
				<p className="mt-1 max-w-xs text-sm text-muted-foreground">
					Jag kan svara på frågor om uppdrag, kunder, konsulter, avtal och tidrapporter, och göra
					ändringar när du godkänner dem.
				</p>
				<ul className="mt-5 w-full max-w-sm space-y-1.5">
					{SUGGESTED_PROMPTS.map((prompt) => (
						<li key={prompt}>
							<button
								type="button"
								onClick={() => onSuggestion(prompt)}
								className="w-full rounded-lg border border-border bg-white px-3 py-2 text-left text-sm text-foreground hover:border-brand hover:bg-brand/5"
							>
								{prompt}
							</button>
						</li>
					))}
				</ul>
				{previousConversation && onResume && (
					<button
						type="button"
						onClick={() => onResume(previousConversation.id)}
						title="Fortsätt senaste konversationen"
						className="mt-6 flex w-full max-w-sm items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground hover:bg-neutral-100 hover:text-foreground"
					>
						<History className="h-4 w-4 shrink-0" />
						<span className="min-w-0 flex-1">
							<span className="block text-xs">Fortsätt senaste konversationen</span>
							<span className="line-clamp-1 block text-foreground">
								{previousConversation.title ||
									previousConversation.turns.find((turn) => turn.role === "user")?.text ||
									"Ny konversation"}
							</span>
						</span>
						<span className="shrink-0 text-xs text-foreground-subtle">
							{dayjs(previousConversation.updated).format("D MMM HH:mm")}
						</span>
					</button>
				)}
			</div>
		);
	}

	return (
		<div className={cn("space-y-5 px-4 py-4", className)}>
			{conversation.turns.map((turn) => (
				<AiMessage key={turn.id} turn={turn} conversationId={conversation.id} />
			))}

			{pendingCount > 0 && (
				<AiApprovalCard
					key={conversation.pendingApprovals.map((approval) => approval.approvalId).join("|")}
					approvals={conversation.pendingApprovals}
					onRespond={onRespondToApprovals}
					isSubmitting={isApproving}
					className="ml-8"
				/>
			)}

			{isThinking && (
				<div className="flex items-center gap-2.5 text-sm text-muted-foreground">
					<div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand">
						<AiIcon className="h-3.5 w-3.5 animate-pulse" />
					</div>
					<span className="inline-flex items-center gap-1">
						Arbetar
						<span className="inline-flex gap-0.5">
							<span className="h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
							<span className="h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
							<span className="h-1 w-1 animate-bounce rounded-full bg-current" />
						</span>
					</span>
				</div>
			)}

			{error && (
				<div className="ml-8 flex items-start gap-2 rounded-md border border-danger/40 bg-danger-muted px-3 py-2 text-sm text-foreground">
					<AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
					<div className="flex-1">
						<div>{error}</div>
						{onRetry && (
							<button
								type="button"
								onClick={onRetry}
								className="mt-1 text-xs font-medium underline underline-offset-2"
							>
								Försök igen
							</button>
						)}
					</div>
				</div>
			)}

			<div ref={bottomRef} />
		</div>
	);
};

export default AiConversation;
